/**
 * Eval/journey attachment authoring — the push (local path → ref + bytes) and pull
 * (ref → local path + download) halves of gh #3365 for the CLI.
 *
 * ON DISK a turn's attachments are hub-relative PATH STRINGS
 * (`attachments: ["attachments/receipt.png"]`). ON THE WIRE they are content-addressed
 * REFS (`{hash, file_name, mime_type, file_size}`) plus a payload-level
 * `eval_attachment_files` byte carrier deduped by hash. This module is the one place
 * that translates between the two, so push and pull cannot drift.
 *
 * Bytes live at `{hub_id}/eval-attachments/{sha256}` (resource bucket) — content
 * addressed, shared across scenarios/hubs, reaped with the hub.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import type {
  EvalAttachmentRef,
  HubAsCodeEvalAttachmentFile,
  HubAsCodePayload,
} from '@wayai/core/contracts';
import { computeHash, guessMimeType, MAX_RESOURCE_FILE_SIZE } from './resource-files.js';
import { realpathContained, ensureRealSubdirNoSymlink, writeFileNoFollow } from './fs-safety.js';
import { expected } from './parser.js';

/** Canonical on-disk directory for attachment bytes — hub-root, shared by evals + journeys. */
export const ATTACHMENTS_DIR = 'attachments';

/** A turn as it appears in eval `input`/`history` and journey `transcript`. */
export interface AttachableTurn {
  role?: unknown;
  attachments?: unknown;
  [key: string]: unknown;
}

/** Accumulates deduped byte carriers across every turn of a push. */
export type BytesByHash = Map<string, HubAsCodeEvalAttachmentFile>;

/**
 * Resolve a turn's on-disk `attachments` (path strings) to wire refs, registering the
 * bytes in `bytesByHash`. Returns the ref array (or undefined if the turn had none),
 * so the caller can replace the turn's `attachments` with it.
 *
 * Hard-errors (naming the file) on: attachments on a non-user turn, a non-string entry,
 * a path escaping the hub folder, a missing file, or one over the size cap — a silently
 * dropped eval attachment changes what the scenario tests, so unlike the resource
 * scanner's skip-warn, every failure here is loud.
 */
export function resolveTurnAttachments(
  hubFolder: string,
  turn: AttachableTurn,
  label: string,
  bytesByHash: BytesByHash,
): EvalAttachmentRef[] | undefined {
  const raw = turn.attachments;
  if (raw === undefined || raw === null) return undefined;
  if (!Array.isArray(raw) || raw.length === 0) {
    if (Array.isArray(raw)) return undefined; // empty array → no attachments
    throw expected(`${label}: "attachments" must be a list of file paths.`);
  }
  if (turn.role !== 'user') {
    throw expected(`${label}: attachments are only allowed on user turns (this turn is "${String(turn.role)}").`);
  }

  const refs: EvalAttachmentRef[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'string' || !entry.trim()) {
      throw expected(`${label}: each attachment must be a hub-relative file path string.`);
    }
    const abs = path.resolve(hubFolder, entry);
    const root = path.resolve(hubFolder);
    if (abs !== root && !abs.startsWith(root + path.sep)) {
      throw expected(`${label}: attachment "${entry}" escapes the hub folder.`);
    }
    let stat: fs.Stats | undefined;
    try {
      stat = fs.statSync(abs);
    } catch {
      /* ENOENT falls through to the not-found throw below */
    }
    if (!stat?.isFile()) {
      throw expected(`${label}: attachment "${entry}" not found.`);
    }
    // Symlink-escape guard: statSync/readFileSync FOLLOW symlinks, so lexical containment is
    // not enough — a workspace `attachments/x.png` symlinked to `~/.ssh/id_ed25519` would be
    // read and uploaded. Re-check containment on the REAL path (resolves every symlink).
    if (!realpathContained(root, abs)) {
      throw expected(`${label}: attachment "${entry}" resolves outside the hub folder (symlinks are not allowed).`);
    }
    if (stat.size > MAX_RESOURCE_FILE_SIZE) {
      throw expected(
        `${label}: attachment "${entry}" is ${(stat.size / 1024 / 1024).toFixed(1)}MB, over the 10MB limit.`,
      );
    }

    const data = fs.readFileSync(abs);
    const hash = computeHash(data);
    const fileName = path.basename(abs);
    // mime is REQUIRED on the ref (the LLM file path drops mime-less files); fall back
    // rather than omit.
    const mimeType = guessMimeType(fileName) ?? 'application/octet-stream';

    refs.push({ hash, file_name: fileName, mime_type: mimeType, file_size: stat.size });
    if (!bytesByHash.has(hash)) {
      bytesByHash.set(hash, {
        hash,
        content_base64: data.toString('base64'),
        file_name: fileName,
        mime_type: mimeType,
        file_size: stat.size,
      });
    }
  }
  return refs;
}

/**
 * Resolve a turn's on-disk attachments and REPLACE `turn.attachments` in place: set the
 * wire refs when present, or strip the key entirely so an empty/absent list never reaches
 * the payload. The single mutation point shared by every eval/journey parse site.
 */
export function applyTurnAttachments(
  hubFolder: string,
  turn: AttachableTurn,
  label: string,
  bytesByHash: BytesByHash,
): void {
  const refs = resolveTurnAttachments(hubFolder, turn, label, bytesByHash);
  if (refs) turn.attachments = refs;
  else delete turn.attachments;
}

/**
 * Invariant guard for the YAML builders (which emit `attachments` verbatim): on
 * disk, a turn's attachments are hub-relative path STRINGS. A wire-shape ref
 * (object with a `hash` key) reaching serialization means a write path skipped
 * `rewriteEvalAttachmentsToLocalPaths` (gh #3410) — fail loud before poisoning a
 * local file the next push would reject.
 */
export function assertAttachmentsAreLocalPaths(turn: unknown, label: string): void {
  const attachments = (turn as AttachableTurn | null | undefined)?.attachments;
  if (!Array.isArray(attachments)) return;
  for (const entry of attachments) {
    if (entry !== null && typeof entry === 'object' && 'hash' in entry) {
      throw new Error(
        `wire-shape attachment ref reached YAML serialization — CLI bug (${label}): ` +
        'the payload was not run through materializeHubFolder before writing.',
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Pull side — refs → local paths + download
// ---------------------------------------------------------------------------

/** One file to fetch on pull: signed URL → local hub-relative path. */
export interface AttachmentDownload {
  relPath: string;
  url: string;
  hash: string;
}

/**
 * Outcome of a `downloadEvalAttachments` pass. `written` is the count actually fetched
 * (byte-identical files are skipped); `changed`/`removed` are the hub-relative paths written
 * and orphan-pruned, so a caller can NAME every working-tree mutation (gh #3410).
 */
export interface AttachmentSyncResult {
  written: number;
  changed: string[];
  removed: string[];
}

/** Iterate every eval/journey user turn that can carry attachments, in place. */
function forEachAttachableTurn(payload: HubAsCodePayload, fn: (turn: AttachableTurn) => void): void {
  for (const ev of payload.evals ?? []) {
    if (ev.input) fn(ev.input as AttachableTurn);
    for (const t of ev.history ?? []) fn(t as AttachableTurn);
  }
  for (const jr of payload.journeys ?? []) {
    for (const t of jr.transcript ?? []) fn(t as AttachableTurn);
  }
}

/**
 * Rewrite each turn's attachment REFS to on-disk PATH STRINGS, and return the deduped
 * download list. MUST run before the YAML is written — the writer emits `attachments`
 * verbatim, so leaving refs on the turns would persist wire shapes to disk and break the
 * pull → push round-trip.
 *
 * The local file NAME is always `path.basename(file_name)`. `file_name` is server-stored
 * and UNTRUSTED (REST/MCP eval bodies are passthrough; `evalAttachmentRef` bounds only its
 * length, not its charset), so a value like `../hub.yaml` must not survive into the on-disk
 * path — it would resolve out of `attachments/` and overwrite a sibling workspace file on
 * download. The local path is keyed by (hash, name) so identical bytes authored under two
 * different names round-trip as two files; two DISTINCT hashes sharing a name get a
 * `<stem>-<hash8><ext>` suffix so neither clobbers the other.
 */
export function rewriteEvalAttachmentsToLocalPaths(payload: HubAsCodePayload): AttachmentDownload[] {
  const urlByHash = new Map((payload.eval_attachment_files ?? []).map((f) => [f.hash, f.download_url]));
  // The on-disk name: strip every directory component from the untrusted file_name. A
  // degenerate basename that denotes no real file — `.`, `..`, or `` (an all-separator
  // name like `/` or `//`) — falls back to the hash, so the path can never resolve to the
  // attachments/ directory itself (which would crash the download on readFileSync/EISDIR).
  const safeName = (ref: EvalAttachmentRef): string => {
    const base = path.basename(ref.file_name);
    return base === '' || base === '.' || base === '..' ? ref.hash.slice(0, 16) : base;
  };
  // Detect collisions across distinct hashes that share the same on-disk name.
  const hashesByName = new Map<string, Set<string>>();
  const collect = (turn: AttachableTurn) => {
    if (!Array.isArray(turn.attachments)) return;
    for (const ref of turn.attachments as EvalAttachmentRef[]) {
      if (!ref?.hash || !ref.file_name) continue;
      const name = safeName(ref);
      let set = hashesByName.get(name);
      if (!set) { set = new Set(); hashesByName.set(name, set); }
      set.add(ref.hash);
    }
  };
  forEachAttachableTurn(payload, collect);

  // One local file per (hash, name): same bytes under two names → two round-tripping files;
  // same (hash, name) → one shared file.
  const pathByKey = new Map<string, string>();
  // Uniqueness is tracked CASE-INSENSITIVELY: macOS/Windows filesystems collapse case, so
  // `Receipt.png` and `receipt.png` address the SAME file — the second download would
  // otherwise overwrite the first and drop an attachment.
  const usedLower = new Set<string>();
  const localPathFor = (ref: EvalAttachmentRef): string => {
    const name = safeName(ref);
    const key = `${ref.hash} ${name}`;
    const existing = pathByKey.get(key);
    if (existing) return existing;
    const ext = path.extname(name);
    const stem = path.basename(name, ext);
    const collides = (hashesByName.get(name)?.size ?? 0) > 1;
    let rel = collides ? `${ATTACHMENTS_DIR}/${stem}-${ref.hash.slice(0, 8)}${ext}` : `${ATTACHMENTS_DIR}/${name}`;
    // GLOBAL uniqueness: a distinct (hash, name) must never reuse a path already assigned —
    // e.g. a file legitimately named like another file's generated `<stem>-<hash8>` suffix,
    // or one differing only by case — would otherwise collapse both into one download and
    // silently drop one attachment. Lengthen the hash suffix (then a counter) until free.
    if (usedLower.has(rel.toLowerCase())) {
      let n = 12;
      while (usedLower.has(rel.toLowerCase()) && n <= ref.hash.length) {
        rel = `${ATTACHMENTS_DIR}/${stem}-${ref.hash.slice(0, n)}${ext}`;
        n += 4;
      }
      for (let extra = 2; usedLower.has(rel.toLowerCase()); extra++) {
        rel = `${ATTACHMENTS_DIR}/${stem}-${ref.hash}-${extra}${ext}`;
      }
    }
    usedLower.add(rel.toLowerCase());
    pathByKey.set(key, rel);
    return rel;
  };

  const downloads = new Map<string, AttachmentDownload>();
  forEachAttachableTurn(payload, (turn) => {
    if (!Array.isArray(turn.attachments)) return;
    turn.attachments = (turn.attachments as EvalAttachmentRef[]).map((ref) => {
      const rel = localPathFor(ref);
      const url = urlByHash.get(ref.hash);
      // Dedup by DESTINATION path — the same bytes may land at two names.
      if (url && !downloads.has(rel)) downloads.set(rel, { relPath: rel, url, hash: ref.hash });
      return rel;
    }) as unknown as EvalAttachmentRef[];
  });
  // The byte carrier is a wire-only field — never write it to disk.
  delete payload.eval_attachment_files;

  return [...downloads.values()];
}

/**
 * Fetch attachment bytes into the hub folder, then prune orphans from `attachments/`.
 * Skips a download when the local file already matches by content hash (keeps repeated
 * pulls cheap and the tree clean). Mirrors `downloadBinaryFiles` for resources.
 *
 * Symlink safety: `writeFileSync` and `readdir`+`rmSync` FOLLOW symlinks, so before any
 * read/write/prune we verify `attachments/` resolves to a real directory INSIDE the hub
 * folder, and we never write through a symlinked destination (a pre-planted link could
 * otherwise overwrite an external file). Belt-and-braces atop the basename sanitization in
 * `rewriteEvalAttachmentsToLocalPaths`.
 */
export async function downloadEvalAttachments(
  hubFolder: string,
  downloads: AttachmentDownload[],
): Promise<AttachmentSyncResult> {
  const attachDir = path.resolve(hubFolder, ATTACHMENTS_DIR);
  const result: AttachmentSyncResult = { written: 0, changed: [], removed: [] };

  // attachments/ must be reachable from the hub folder through no symlinked component (a
  // symlinked attachments/ — even to a not-yet-existent target — would otherwise redirect
  // writes/prunes outside the workspace). Create it (real) only when there's something to
  // download.
  if (!ensureRealSubdirNoSymlink(hubFolder, attachDir, downloads.length > 0)) {
    console.warn(`  Warning: ${ATTACHMENTS_DIR}/ is not reachable inside the hub folder without a symlink; skipping attachment sync.`);
    return result;
  }
  if (!fs.existsSync(attachDir)) return result; // nothing downloaded and nothing to prune

  for (const dl of downloads) {
    const abs = path.resolve(hubFolder, dl.relPath);
    // Containment boundary is attachments/, NOT the hub-folder root: a single `../` in a
    // (server-stored, untrusted) file_name stays inside the hub folder and would otherwise
    // overwrite a sibling like hub.yaml. rewriteEvalAttachmentsToLocalPaths already
    // basenames the name; this is the belt-and-braces write guard.
    if (abs !== attachDir && !abs.startsWith(attachDir + path.sep)) continue;

    // Skip re-download when the local REAL file already matches (lstat: never hash through a
    // symlink; a symlinked destination falls through to the no-follow write below).
    let existing: fs.Stats | undefined;
    try { existing = fs.lstatSync(abs); } catch { /* absent */ }
    if (existing?.isFile() && !existing.isSymbolicLink() && computeHash(fs.readFileSync(abs)) === dl.hash) {
      continue;
    }

    try {
      const res = await fetch(dl.url);
      if (!res.ok) {
        console.warn(`  Warning: failed to download ${dl.relPath} (HTTP ${res.status})`);
        continue;
      }
      if (writeFileNoFollow(hubFolder, abs, Buffer.from(await res.arrayBuffer()))) {
        result.written++;
        result.changed.push(dl.relPath);
      }
    } catch (err) {
      console.warn(`  Warning: failed to download ${dl.relPath}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // Orphan-clean: remove any file (or stray symlink) in attachments/ not referenced by this
  // pull. lstat so a symlinked entry is removed as a LINK, never followed.
  const keep = new Set(downloads.map((d) => path.resolve(hubFolder, d.relPath)));
  for (const name of fs.readdirSync(attachDir)) {
    const abs = path.join(attachDir, name);
    const st = fs.lstatSync(abs);
    if ((st.isFile() || st.isSymbolicLink()) && !keep.has(abs)) {
      fs.rmSync(abs);
      result.removed.push(path.relative(hubFolder, abs));
    }
  }

  return result;
}
